import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const isHeroTransparent = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHeroTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-background-200/80 shadow-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <span
              className={`font-heading text-xl font-bold tracking-tight transition-colors duration-300 ${
                isHeroTransparent ? 'text-white' : 'text-foreground-950'
              }`}
            >
              CSSBuy<span className={isHeroTransparent ? 'text-white/90' : 'text-primary-500'}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isHeroTransparent
                  ? 'text-white/85 hover:text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('navHome')}
            </Link>
            <Link
              to="/guia-cssbuy-spreadsheet"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isHeroTransparent
                  ? 'text-white/85 hover:text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('navShoppingGuide')}
            </Link>
            <Link
              to="/#categories"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isHeroTransparent
                  ? 'text-white/85 hover:text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('navCategories')}
            </Link>
            <Link
              to="/#articles"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isHeroTransparent
                  ? 'text-white/85 hover:text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('navArticles')}
            </Link>
            <Link
              to="/#faq"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                isHeroTransparent
                  ? 'text-white/85 hover:text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('navFAQ')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`ml-2 px-5 py-2 text-sm font-semibold whitespace-nowrap rounded-md transition-colors duration-200 cursor-pointer ${
                isHeroTransparent
                  ? 'bg-white text-foreground-900 hover:bg-white/90'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              {t('ctaCssbuySheet')}
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              isHeroTransparent
                ? 'text-white hover:bg-white/15'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              <i className="ri-home-4-line text-lg text-primary-500"></i>
              {t('navHome')}
            </Link>
            <Link
              to="/guia-cssbuy-spreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              <i className="ri-book-open-line text-lg text-primary-500"></i>
              {t('navShoppingGuide')}
            </Link>
            <Link
              to="/#categories"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              <i className="ri-grid-line text-lg text-primary-500"></i>
              {t('navCategories')}
            </Link>
            <Link
              to="/#articles"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              <i className="ri-article-line text-lg text-primary-500"></i>
              {t('navArticles')}
            </Link>
            <Link
              to="/#faq"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors duration-200"
            >
              <i className="ri-question-answer-line text-lg text-primary-500"></i>
              {t('navFAQ')}
            </Link>
            <div className="border-t border-background-100 mt-2 pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600 rounded-lg transition-colors duration-200 text-center cursor-pointer"
              >
                <i className="ri-external-link-line text-lg"></i>
                {t('ctaCssbuySheet')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}